/**
 * Accessibility Monitor Utility
 * This utility helps identify and fix common accessibility issues,
 * especially those introduced by third-party scripts and widgets.
 */

// Common invalid ARIA role combinations to check for
const INVALID_ARIA_COMBINATIONS = [
  { element: "div", invalidRoles: ["button", "link", "textbox"] },
  { element: "span", invalidRoles: ["button", "link", "textbox"] },
  { element: "img", invalidRoles: ["button", "link"] },
  { element: "p", invalidRoles: ["button", "link"] },
];

/**
 * Monitors and fixes accessibility issues in the DOM
 */
export class AccessibilityMonitor {
  private observer: MutationObserver | null = null;
  private isMonitoring = false;

  /**
   * Start monitoring the DOM for accessibility issues
   */
  startMonitoring(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    // Initial scan
    this.scanAndFixAccessibilityIssues();

    // Set up mutation observer to catch dynamically added content
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.scanElementForIssues(node as Element);
            }
          });
        }
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Stop monitoring
   */
  stopMonitoring(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.isMonitoring = false;
  }

  /**
   * Scan the entire document for accessibility issues
   */
  private scanAndFixAccessibilityIssues(): void {
    this.scanElementForIssues(document.body);
  }

  /**
   * Scan a specific element and its children for accessibility issues
   */
  private scanElementForIssues(element: Element): void {
    // Check the element itself
    this.fixElementAccessibility(element);

    // Check all children
    const allElements = element.querySelectorAll("*");
    allElements.forEach((el) => this.fixElementAccessibility(el));
  }

  /**
   * Fix accessibility issues for a specific element
   */
  private fixElementAccessibility(element: Element): void {
    const tagName = element.tagName.toLowerCase();
    const role = element.getAttribute("role");

    if (!role) {
      return;
    }

    // Check for invalid ARIA role combinations
    INVALID_ARIA_COMBINATIONS.forEach(({ element: elType, invalidRoles }) => {
      if (tagName === elType && invalidRoles.includes(role)) {
        console.warn(
          `Accessibility Warning: Invalid role "${role}" on ${tagName} element`,
          element,
        );
        this.fixInvalidRole(element, role);
      }
    });

    // Fix missing required ARIA attributes
    this.ensureRequiredAriaAttributes(element, role);

    // Fix third-party widget issues
    this.fixThirdPartyWidgetIssues(element);
  }

  /**
   * Fix invalid ARIA roles
   */
  private fixInvalidRole(element: Element, invalidRole: string): void {
    const tagName = element.tagName.toLowerCase();

    // Remove invalid role or replace with appropriate one
    if (invalidRole === "button" && tagName === "div") {
      // If div has role="button", ensure it has proper keyboard support
      if (!element.getAttribute("tabindex")) {
        element.setAttribute("tabindex", "0");
      }
      if (!element.getAttribute("aria-label") && !element.textContent?.trim()) {
        element.setAttribute("aria-label", "Button");
      }
    } else {
      // Remove invalid role
      element.removeAttribute("role");
      console.log(
        `Removed invalid role "${invalidRole}" from ${tagName} element`,
      );
    }
  }

  /**
   * Ensure required ARIA attributes are present
   */
  private ensureRequiredAriaAttributes(element: Element, role: string): void {
    switch (role) {
      case "dialog":
        if (!element.getAttribute("aria-modal")) {
          element.setAttribute("aria-modal", "true");
        }
        if (
          !element.getAttribute("aria-labelledby") &&
          !element.getAttribute("aria-label")
        ) {
          element.setAttribute("aria-label", "Dialog");
        }
        break;

      case "button":
        if (
          !element.getAttribute("aria-label") &&
          !element.textContent?.trim()
        ) {
          element.setAttribute("aria-label", "Button");
        }
        break;

      case "status":
        if (!element.getAttribute("aria-live")) {
          element.setAttribute("aria-live", "polite");
        }
        break;

      case "alert":
        if (!element.getAttribute("aria-live")) {
          element.setAttribute("aria-live", "assertive");
        }
        break;
    }
  }

  /**
   * Fix common third-party widget accessibility issues
   */
  private fixThirdPartyWidgetIssues(element: Element): void {
    // Fix Intercom widget issues
    if (
      element.id === "intercom-frame" ||
      element.classList.contains("intercom-lightweight-app")
    ) {
      if (!element.getAttribute("title")) {
        element.setAttribute("title", "Customer support chat");
      }
      if (!element.getAttribute("aria-label")) {
        element.setAttribute("aria-label", "Live chat support");
      }
    }

    // Fix other common third-party widget issues
    if (
      element.tagName.toLowerCase() === "iframe" &&
      !element.getAttribute("title")
    ) {
      const src = element.getAttribute("src") || "";
      if (src.includes("intercom.io")) {
        element.setAttribute("title", "Intercom live chat");
      } else if (src.includes("google.com")) {
        element.setAttribute("title", "Google embedded content");
      } else {
        element.setAttribute("title", "Embedded content");
      }
    }
  }
}

// Create singleton instance
export const accessibilityMonitor = new AccessibilityMonitor();

/**
 * Initialize accessibility monitoring
 * Call this function after your app has loaded
 * Only runs in development mode
 */
export const initializeAccessibilityMonitoring = (): void => {
  if (typeof window !== "undefined" && import.meta.env.DEV) {
    // Start monitoring after a short delay to allow other scripts to load
    setTimeout(() => {
      accessibilityMonitor.startMonitoring();
    }, 1000);
  }
};
